import React, {useEffect, useState} from "react";
import {Box, Button, Checkbox, FormControlLabel, Modal, TextField, Typography} from "@mui/material";
import "../admin/Admin.css";
import {newRoom, Room} from "../../data/rooms";
import {addRoom} from "../../services/scheduleService";

interface AddRoomModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
    refreshRooms: () => void;
}

// Styling similar to the Edit Modal
const modalStyle = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
};

const AddRoomModal: React.FC<AddRoomModalProps> = ({
                                                       isOpen,
                                                       onClose,
                                                       eventId,
                                                       refreshRooms
                                                   }) => {
    const [room, setRoom] = useState<Room>(newRoom());
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Validation states
    const [errors, setErrors] = useState({
        RoomId: '',
        Title: '',
        Description: '',
        EventId: '',
        ShowDescription: false,
        Date: '',
    });

    useEffect(() => {
        if (isOpen) {
            setRoom(newRoom()); // This should generate a fresh RoomId
            setErrors({
                RoomId: '',
                Title: '',
                Description: '',
                EventId: '',
                ShowDescription: false,
                Date: '',
            });
            setErrorMessage(null);
        }
    }, [isOpen]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const {name, type, value} = e.target;

        const newValue =
            type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value;

        setRoom((prev) => ({
            ...prev,
            [name]: newValue,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: "",
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setErrorMessage(null);

        try {
            room.EventId = eventId;

            const response = await addRoom(room);
            console.log("Room added successfully:", response);

            refreshRooms();
            onClose();
        } catch (error) {
            console.error("Submit Error:", error);

            const fallback = "Failed to add room. Please try again.";
            const message = error instanceof Error ? error.message : fallback;
            setErrorMessage(message);

        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="add-room-modal">
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h2">
                    Add A New Room
                </Typography>
                <Box sx={{mt: 2}}>
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Title"
                        name="Title"
                        value={room.Title}
                        onChange={handleChange}
                        error={Boolean(errors.Title)}
                        helperText={errors.Title}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Date"
                        name="Date"
                        type="date"
                        value={room.Date || ''}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(errors.Date)}
                        helperText={errors.Date}
                    />
                    <TextField
                        fullWidth
                        margin="dense"
                        label="Description"
                        name="Description"
                        multiline
                        rows={3}
                        value={room.Description}
                        onChange={handleChange}
                        error={Boolean(errors.Description)}
                        helperText={errors.Description}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                name="ShowDescription"
                                checked={room.ShowDescription}
                                onChange={handleChange}
                            />
                        }
                        label="Show Description"
                    />
                    {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                </Box>
                <Box sx={{mt: 3, display: "flex", justifyContent: "flex-end"}}>
                    <Button onClick={onClose} sx={{mr: 1}}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" className="editOrAdd" disabled={loading}>
                        {loading ? "Submitting..." : "Submit"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddRoomModal;
